import type { InjectionKey, Ref } from "vue";
import { inject, ref } from "vue";

/**
 * Marks an element in a dropdown as a menu item: `data-menu-item` in a template. VDropdownItem sets it; a custom
 * item (such as the choices of ChangeableItem) sets it together with a `menuitem`, `menuitemradio` or
 * `menuitemcheckbox` role and `tabindex="-1"`.
 */
export const MENU_ITEM_ATTRIBUTE = "data-menu-item";

export const FOCUSABLE_SELECTOR = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable='true'], [tabindex]:not([tabindex='-1'])";

export interface DropdownMenuContext {
  /** True while the dropdown holds menu items and nothing else that takes focus, so it acts as a menu. */
  isMenu: Readonly<Ref<boolean>>
}

export const DROPDOWN_MENU_KEY: InjectionKey<DropdownMenuContext> = Symbol("DropdownMenu");

const NEXT_KEYS = new Set(["ArrowDown"]);
const PREVIOUS_KEYS = new Set(["ArrowUp"]);

export function menuItemsOf(container: Element): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(`[${MENU_ITEM_ATTRIBUTE}]`));
}

/**
 * A dropdown is a menu when it holds menu items and no other control: a search field, a form or a list of
 * buttons next to the items keeps it a plain popover in which every item is a button in the tab order.
 */
export function isMenuContent(container: Element): boolean {
  const items = menuItemsOf(container);
  if (items.length === 0) {
    return false;
  }
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
    .every(element => element === container || items.some(item => item === element || item.contains(element)));
}

/** The item to focus when a menu opens: the checked choice, otherwise the first item. */
export function initialMenuItem(container: Element): HTMLElement | undefined {
  const items = menuItemsOf(container);
  return items.find(item => item.getAttribute("aria-checked") === "true") ?? items[0];
}

/** Moves focus between the items of an open menu: the arrow keys wrap around, Home and End jump to the ends. */
export function focusMenuItemForKey(container: Element, key: string, current: Element | null): boolean {
  const items = menuItemsOf(container);
  if (items.length === 0) {
    return false;
  }
  const position = items.findIndex(item => item === current || item.contains(current));
  let target: HTMLElement | undefined;
  if (key === "Home") {
    target = items[0];
  } else if (key === "End") {
    target = items[items.length - 1];
  } else if (NEXT_KEYS.has(key)) {
    target = items[(position + 1) % items.length];
  } else if (PREVIOUS_KEYS.has(key)) {
    target = items[position <= 0 ? items.length - 1 : position - 1];
  }
  if (!target) {
    return false;
  }
  target.focus();
  return true;
}

/** The menu item that contains the element, if any. */
export function closestMenuItem(element: EventTarget | null): HTMLElement | null {
  return element instanceof Element ? element.closest<HTMLElement>(`[${MENU_ITEM_ATTRIBUTE}]`) : null;
}

/**
 * For an item of a dropdown: whether that dropdown currently acts as a menu. Outside a dropdown, or in a
 * dropdown with other controls, an item is a button of its own.
 */
export function useDropdownMenuItem(): Readonly<Ref<boolean>> {
  return inject(DROPDOWN_MENU_KEY, null)?.isMenu ?? ref(false);
}
